import { useState } from "react"
import { Box, Chip, CircularProgress, IconButton, Stack, Tooltip, Typography } from "@mui/material"
import DownloadIcon from "@mui/icons-material/FileDownloadOutlined"
import DeleteIcon from "@mui/icons-material/DeleteOutlineOutlined"
import ImageIcon from "@mui/icons-material/ImageOutlined"
import PdfIcon from "@mui/icons-material/PictureAsPdfOutlined"
import SheetIcon from "@mui/icons-material/TableChartOutlined"
import DocIcon from "@mui/icons-material/DescriptionOutlined"
import ArchiveIcon from "@mui/icons-material/FolderZipOutlined"
import FileIcon from "@mui/icons-material/InsertDriveFileOutlined"
import EditIcon from "@mui/icons-material/EditOutlined"

import { downloadFile, fileKind } from "../../api/uploads"

const ICONS = { image: ImageIcon, pdf: PdfIcon, sheet: SheetIcon, doc: DocIcon, archive: ArchiveIcon, file: FileIcon }

/**
 * One attachment, as a row with a download button.
 *
 * Used for everything that is not an inline image — and for images too, next to
 * the preview, because "see it" and "keep it" are different actions.
 */
export default function FileChip({ file, onRemove, dense = false }) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  const Icon = ICONS[fileKind(file.mime_type, file.file_name)] || FileIcon

  const download = async () => {
    setBusy(true)
    setError(null)
    try {
      await downloadFile(file.client_id, file.file_id ?? file.id, file.file_name)
    } catch (err) {
      setError(err.message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{
        px: 1.25,
        py: dense ? 0.5 : 0.75,
        borderRadius: 1.5,
        border: 1,
        borderColor: error ? "error.main" : "divider",
        bgcolor: "action.hover",
        maxWidth: 380,
        minWidth: 0,
      }}
    >
      <Icon fontSize="small" sx={{ color: "text.secondary", flexShrink: 0 }} />

      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="body2" noWrap title={file.file_name}>
          {file.file_name}
        </Typography>
        <Typography variant="caption" color={error ? "error.main" : "text.secondary"}>
          {error || file.size_label || ""}
        </Typography>
      </Box>

      <Tooltip title="Download">
        <span>
          <IconButton size="small" onClick={download} disabled={busy}>
            {busy ? <CircularProgress size={16} /> : <DownloadIcon fontSize="small" />}
          </IconButton>
        </span>
      </Tooltip>

      {onRemove ? (
        <Tooltip title="Remove">
          <IconButton size="small" onClick={() => onRemove(file)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : null}
    </Stack>
  )
}

/** A file that has been chosen but not yet sent, with its own remove button. */
/**
 * @param {function} [onEdit] Offered for an image. A staged image is the last
 *   moment anything can be cropped or redacted, because after this it is on the
 *   server — so the affordance belongs here rather than only at the moment of
 *   picking the file.
 */
export function PendingFileChip({ file, onRemove, onEdit, progress }) {
  const Icon = ICONS[fileKind(file.type, file.name)] || FileIcon
  const editable = onEdit && String(file.type || "").startsWith("image/")

  return (
    <Chip
      size="small"
      icon={<Icon sx={{ fontSize: 15 }} />}
      label={
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Typography variant="caption" noWrap sx={{ maxWidth: 170 }}>
            {file.name}
          </Typography>
          {editable ? (
            <Tooltip title="Crop, rotate or redact before sending">
              <Box
                component="span"
                role="button"
                aria-label={`Edit ${file.name}`}
                onClick={(event) => {
                  event.stopPropagation()
                  onEdit(file)
                }}
                sx={{ display: "inline-flex", cursor: "pointer", color: "primary.dark" }}
              >
                <EditIcon sx={{ fontSize: 14 }} />
              </Box>
            </Tooltip>
          ) : null}
          {typeof progress === "number" && progress < 100 ? (
            <CircularProgress size={11} variant="determinate" value={progress} />
          ) : null}
        </Stack>
      }
      onDelete={onRemove}
      sx={{ maxWidth: 280 }}
    />
  )
}
